import { ChangeDetectionStrategy, Component, inject, Input, OnChanges } from '@angular/core'

import { CiecTranslateService } from '@shared/services/ciec-translate.service'
import { SyntageRatios } from '@data/interfaces/risk-scorecard.interface'

/** Grupo de ratios financieros de Syntage: Liquidez, Rentabilidad, Apalancamiento, Actividad. */
export type RatioGroup = 'liquidity' | 'profitability' | 'leverage' | 'activity'

/** Un ratio en su fila de la tabla del grupo: el valor de cada año y el más reciente («Último»). */
export interface RatioCard {
  key: string
  label: string
  latest: string
  years: Array<{ year: string; value: string }>
}

/** Un grupo de ratios: sus filas y los ratios que no traen ningún valor, que van en una línea debajo de la tabla. */
export interface RatioGroupView {
  key: RatioGroup
  label: string
  cards: RatioCard[]
  missing: string[]
}

const RATIO_GROUPS: ReadonlyArray<{ key: RatioGroup; label: string }> = [
  { key: 'liquidity', label: 'Liquidez' },
  { key: 'profitability', label: 'Rentabilidad' },
  { key: 'leverage', label: 'Apalancamiento' },
  { key: 'activity', label: 'Actividad' },
]

/**
 * Los ratios financieros que calcula Syntage, una tabla densa por grupo en rejilla de 2. Vivían en la sub-pestaña
 * Análisis y se movieron sin cambios a la pestaña Ratios (D-42), debajo de los de Rendafin, como referencia.
 * Presentacional: recibe los ratios y no pide nada.
 */
@Component({
  selector: 'app-syntage-ratios',
  templateUrl: './syntage-ratios.component.html',
  styleUrls: ['./syntage-ratios.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SyntageRatiosComponent implements OnChanges {
  /** Grupo › ratio › año. */
  @Input() ratios: SyntageRatios | null = null

  groups: RatioGroupView[] = []
  years: string[] = []

  private readonly t = inject(CiecTranslateService)

  ngOnChanges(): void {
    this.years = yearsOf(this.ratios)
    this.groups = this.buildGroups()
  }

  trackByIdx(idx: number): number {
    return idx
  }

  /**
   * Las filas de cada grupo. Un ratio sin ningún valor en ningún año no lleva fila: va en «Sin dato:». Un grupo sin
   * ninguna fila no se pinta. Sin colores de bueno o malo: el panel no sabe si subir es bueno en cada ratio.
   */
  private buildGroups(): RatioGroupView[] {
    const show = (value: number | null) => (value === null ? '—' : this.t.formatRatio(value))
    return RATIO_GROUPS.map(({ key, label }) => {
      const group = this.ratios?.[key] ?? {}
      const cards: RatioCard[] = []
      const missing: string[] = []
      for (const ratio of Object.keys(group)) {
        const years = this.years.map((year) => ({ year, value: valueFor(group[ratio], year) }))
        const latest = latestValue(group[ratio])
        const name = this.t.translateMetricKey(ratio)
        if (latest === null && years.every((point) => point.value === null)) missing.push(name)
        else cards.push({ key: ratio, label: name, latest: show(latest), years: years.map((point) => ({ year: point.year, value: show(point.value) })) })
      }
      return { key, label, cards, missing }
    }).filter((group) => group.cards.length > 0)
  }
}

/** Años presentes en cualquiera de los ratios, de menor a mayor. */
function yearsOf(ratios: SyntageRatios | null): string[] {
  const years = new Set<string>()
  for (const group of Object.values(ratios ?? {})) {
    if (!group || typeof group !== 'object') continue
    for (const ratio of Object.values(group)) if (ratio && typeof ratio === 'object') for (const year of Object.keys(ratio)) if (!isNaN(Number(year))) years.add(year)
  }
  return [...years].sort()
}

/** Valor del ratio en el año más reciente que trae. */
function latestValue(ratio: Record<string, number | null> | null | undefined): number | null {
  if (!ratio || typeof ratio !== 'object') return null
  const years = Object.keys(ratio).filter((year) => !isNaN(Number(year)))
  if (years.length === 0) return null
  return valueFor(ratio, years.sort().reverse()[0])
}

function valueFor(ratio: Record<string, number | null> | null | undefined, year: string): number | null {
  const value = ratio?.[year]
  return value == null || isNaN(value) ? null : Number(value)
}
